import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, merge, timer } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { APPROVAL_FLOW_NOTIFICATIONS_SIZE } from '../config/approval-flow.constants';
import { destinoDeDecision, destinoDeFlujo } from '../utils/approval-destination.utils';
import { POLL_INTERVAL_MS } from '../config/ui.constants';
import { PendingApprovalItem } from '../models/approval-flow.model';
import { AppNotification, AppNotificationProvider, AppNotificationSource } from '../models/notification-center.model';
import { approvalActorName } from '../utils/approval-flow-view.utils';
import { conNombreDeSucursal } from '../utils/sucursal.utils';
import { SucursalesService } from './sucursales.service';
import { ApprovalFlowService } from './approval-flow.service';
import { AuthService } from './auth.service';

/**
 * Lo que espera la decisión del usuario en sesión — FUENTE ÚNICA del aviso.
 *
 * La campanita del encabezado lee estas señales; el sondeo vive aquí y no en el
 * componente para que no se reinicie cada vez que el encabezado se vuelve a pintar.
 *
 * El API resuelve de quién son los pendientes con la cabecera `X-Acting-User` que pone
 * `authInterceptor`: aquí no se manda ningún usuario.
 */
@Injectable({ providedIn: 'root' })
export class ApprovalNotificationsService implements AppNotificationProvider {
    readonly source: AppNotificationSource = 'approvals';
    private readonly approvalFlow = inject(ApprovalFlowService);
    private readonly sucursales = inject(SucursalesService);
    private readonly auth = inject(AuthService);

    /** Lo último que se pintó, para no re-emitir si el API devuelve lo mismo. */
    private firma = '';

    private readonly refresh$ = new Subject<void>();
    private readonly items = signal<readonly PendingApprovalItem[]>([]);
    private readonly cargando = signal(false);
    private readonly fallo = signal(false);

    readonly loading = this.cargando.asReadonly();
    /** El último sondeo falló (llave inválida, API caído…); la campanita no inventa un 0. */
    readonly failed = this.fallo.asReadonly();

    /** La primera consulta de la sesión ya volvió, con avisos o sin ellos. */
    private readonly primeraRespuesta = signal(false);
    readonly consultado = this.primeraRespuesta.asReadonly();

    /**
     * Lo pendiente, ya convertido en avisos de la bandeja. El `id` incluye la versión del
     * contenido a propósito: si la oferta se corrige, el API invalida las aprobaciones y
     * este aviso tiene que volver a aparecer como no leído.
     */
    notifications(): readonly AppNotification[] {
        return this.avisos();
    }

    private readonly avisos = computed<readonly AppNotification[]>(() =>
        this.items().map((item) => {
            const nombres = this.sucursales.nombrePorCodigo();
            // Un solo cálculo por aviso: de él salen el texto y el icono. El destino es
            // siempre la bandeja, con el flujo abierto: el aviso existe para decidir.
            const destino = destinoDeFlujo(item.processName, item.processId);
            const decision = destinoDeDecision(item.processName, item.processId, item.approvalFlowId);
            const solicitante = approvalActorName(item.requesterDisplayName, item.requesterPrincipalId);
            return {
                id: `approvals:${item.approvalFlowId}:v${item.contentVersion}`,
                source: this.source,
                kind: 'approval.pending',
                title: conNombreDeSucursal(
                    item.title?.trim() || `${destino.etiqueta} ${item.processId}`,
                    nombres,
                ),
                detail: `${destino.etiqueta} · espera tu aprobación · solicitó ${solicitante}`,
                occurredAt: item.submittedAt,
                icon: destino.icono,
                tone: 'warning' as const,
                routerLink: decision.commands,
                queryParams: decision.queryParams,
                requiresAction: true,
            };
        }),
    );

    constructor() {
        // El sondeo solo corre con sesión abierta: sin ella el API responde 401 y no hay
        // nada que avisar. Al cerrar sesión el timer se cancela y el contador se limpia.
        const sondeo$ = toObservable(this.auth.isAuthenticated).pipe(
            tap((autenticado) => {
                if (!autenticado) {
                    this.limpiar();
                }
            }),
            switchMap((autenticado) => (autenticado ? timer(0, POLL_INTERVAL_MS.approvals) : EMPTY)),
            map(() => undefined),
        );

        merge(sondeo$, this.refresh$)
            .pipe(
                switchMap(() => this.consultar()),
                takeUntilDestroyed(),
            )
            .subscribe();
    }

    /** Vuelve a consultar ahora (al abrir el panel o después de decidir sobre un flujo). */
    refresh(): void {
        this.refresh$.next();
    }

    private consultar() {
        this.cargando.set(true);
        return this.approvalFlow
            .getPendingApprovals({ size: APPROVAL_FLOW_NOTIFICATIONS_SIZE })
            .pipe(
                tap((pagina) => {
                    const items = pagina.items ?? [];
                    this.cargando.set(false);
                    this.fallo.set(false);
                    this.primeraRespuesta.set(true);
                    this.aplicar(items);
                }),
                catchError(() => {
                    this.cargando.set(false);
                    this.fallo.set(true);
                    // Un fallo también es respuesta: no deja a nadie esperando para siempre.
                    this.primeraRespuesta.set(true);
                    // No se vacía la lista: lo último conocido es mejor que un 0 falso.
                    return EMPTY;
                }),
            );
    }

    /**
     * Solo emite si algo cambió de verdad. Sin esta comparación, cada sondeo crearía un
     * arreglo nuevo y dispararía la detección de cambios del encabezado cada minuto.
     */
    private aplicar(items: readonly PendingApprovalItem[]): void {
        const firma = `${items.length}#${items
            .map((item) => `${item.approvalFlowId}:v${item.contentVersion}:${item.submittedAt}`)
            .join('|')}`;
        if (firma === this.firma) {
            return;
        }
        this.firma = firma;
        this.items.set(items);
        if (items.length) {
            this.sucursales.asegurarCargado();
        }
    }

    private limpiar(): void {
        this.firma = '';
        this.items.set([]);
        this.cargando.set(false);
        this.fallo.set(false);
        // Al cerrar sesión se vuelve a empezar: la próxima entrada saluda de nuevo.
        this.primeraRespuesta.set(false);
    }
}
